// A car's service history: "Service done" from its TUNE MFD and services logged here, newest first by year. Each opens
// to what was done, who did it, what it cost and its parts and labour, to change or delete. Logging one can reset the
// MFD's reminder.
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fromDisplay, METRIC, symbol, toDisplay, type components, type Units } from '@tune/shared';
import { useId, useState, type FormEvent } from 'react';
import { data, problem } from '../../api';
import { useAuth } from '../../auth';
import { grouped, serviceText } from '../../lib/car';
import { useIdempotencyKey } from '../../lib/idempotency';
import { dayText } from '../../lib/words';
import { keys, useUnits } from '../../queries';
import {
  Badge,
  Banner,
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  IconButton,
  ListButton,
  ListSection,
  ListValue,
  Sheet,
  Skeleton,
  SwitchRow,
  TextArea,
  TextField,
  useConfirm,
  useRouteSheet,
  useToast,
} from '../../ui';
import { CaretRightIcon, PlusIcon, TrashIcon, WrenchIcon } from '../../ui/icons';
import { CarPage } from './CarPage';
import s from './Service.module.css';
import { dateField, lineWords, linesTotal, moneyText, noon, typedAmount } from './service-words';

type Vehicle = components['schemas']['Vehicle'];
type Record = components['schemas']['ServiceRecord'];
type ServiceIn = components['schemas']['ServiceIn'];
type ServicePatch = components['schemas']['ServicePatch'];
type Line = components['schemas']['ServiceLine'];

const PAGE = 20;
const chevron = <CaretRightIcon size={16} weight="bold" aria-hidden="true" />;

export function Service() {
  const [logging, setLogging] = useRouteSheet('log-service');
  return (
    <CarPage
      section="Service"
      headActions={
        <Button size="small" icon={<PlusIcon size={16} weight="bold" />} onClick={() => setLogging(true)}>
          Log a service
        </Button>
      }
    >
      {(car) => <ServiceOf car={car} logging={logging} onLogged={() => setLogging(false)} />}
    </CarPage>
  );
}

function ServiceOf({ car, logging, onLogged }: { car: Vehicle; logging: boolean; onLogged: () => void }) {
  const api = useAuth().api('owners');
  const units = useUnits(car.id) ?? METRIC;
  const list = useInfiniteQuery({
    queryKey: keys.service(car.id),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      data(
        api.GET('/vehicles/{vehicleId}/service', {
          params: { path: { vehicleId: car.id }, query: { limit: PAGE, ...(pageParam ? { cursor: pageParam } : {}) } },
        }),
      ),
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const due = serviceText(list.data?.pages[0]?.due ?? null, units);
  const records = list.data?.pages.flatMap((p) => p.records) ?? [];
  // By year, newest first, as they come.
  const years: Array<{ year: string; records: Record[] }> = [];
  for (const r of records) {
    const year = String(new Date(r.doneAt).getFullYear());
    const last = years.at(-1);
    if (last?.year === year) {
      last.records.push(r);
    } else {
      years.push({ year, records: [r] });
    }
  }
  return (
    <>
      <Card title="Next service">
        {due ? (
          <p className={due.overdue ? s.dueLate : s.due}>{due.text}</p>
        ) : (
          <p className={s.text}>The car's TUNE MFD says when the next service is due, once it's driven.</p>
        )}
        <p className={s.text}>
          As the MFD counts it, from the last service: its own "Service done", or one logged here that resets its
          reminder.
        </p>
      </Card>
      {list.isPending && <Skeleton blocks={2} lines={0} label="Loading the service history" />}
      {list.isError && <ErrorBanner retry={() => void list.refetch()}>The service history couldn't load.</ErrorBanner>}
      {list.isSuccess && records.length === 0 && (
        <EmptyState icon={<WrenchIcon size={56} />} title="No services yet">
          "Service done" on the MFD, or a service logged here, shows here.
        </EmptyState>
      )}
      {years.map(({ year, records: these }) => (
        <ListSection key={year} header={year}>
          {these.map((r) => (
            <ServiceRow key={r.id} car={car} record={r} units={units} />
          ))}
        </ListSection>
      ))}
      {list.hasNextPage && (
        <div>
          <Button variant="gray" busy={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
            Show older services
          </Button>
        </div>
      )}
      <LogSheet car={car} units={units} open={logging} onClose={onLogged} />
    </>
  );
}

function titleOf(r: Record): string {
  return r.title ?? (r.source === 'mfd' ? 'Service done' : 'Service');
}

/** A service in the list: what was done, when, by whom and for how much. It opens to the rest. */
function ServiceRow({ car, record, units }: { car: Vehicle; record: Record; units: Units }) {
  const [open, setOpen] = useRouteSheet(`service-${record.id}`);
  const subtitle = [dayText(record.doneAt), record.provider, record.cost !== null ? moneyText(record.cost) : null]
    .filter(Boolean)
    .join(' · ');
  return (
    <>
      <ListButton
        label={titleOf(record)}
        subtitle={subtitle}
        detail={record.source === 'mfd' ? <Badge>From the MFD</Badge> : undefined}
        accessory={chevron}
        onClick={() => setOpen(true)}
      />
      <RecordSheet car={car} record={record} units={units} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/** A service, whole: to read, change or delete. */
function RecordSheet({
  car,
  record,
  units,
  open,
  onClose,
}: {
  car: Vehicle;
  record: Record;
  units: Units;
  open: boolean;
  onClose: () => void;
}) {
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const say = useToast();
  const id = useId();
  const [changing, setChanging] = useState(false);
  const path = { params: { path: { vehicleId: car.id, serviceId: record.id } } };
  const save = useMutation({
    mutationFn: (body: ServicePatch) => data(api.PATCH('/vehicles/{vehicleId}/service/{serviceId}', { ...path, body })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.service(car.id) });
      setChanging(false);
    },
  });
  const remove = useMutation({
    mutationFn: () => data(api.DELETE('/vehicles/{vehicleId}/service/{serviceId}', path)),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: keys.service(car.id) });
      say('The service is deleted.');
    },
  });
  const distance = (km: number) => `${grouped(toDisplay(units, 'distance', km))} ${symbol(units, 'distance')}`;
  const close = () => {
    setChanging(false);
    save.reset();
    remove.reset();
    onClose();
  };
  const ask = async () => {
    const yes = await confirm({
      title: 'Delete this service?',
      message: `${titleOf(record)}, ${dayText(record.doneAt)}, goes from the history for good.`,
      confirm: 'Delete',
    });
    if (yes) {
      remove.mutate();
    }
  };
  if (changing) {
    return (
      <Sheet
        open={open}
        onClose={close}
        title="Change the service"
        size="large"
        action={{ label: 'Save', form: id, busy: save.isPending }}
      >
        <ServiceForm id={id} units={units} record={record} failed={save.error} onSend={(body) => save.mutate(body)} />
      </Sheet>
    );
  }
  return (
    <Sheet
      open={open}
      onClose={close}
      title={titleOf(record)}
      cancelLabel="Close"
      footer={
        <div className={s.sheetActions}>
          <Button variant="gray" onClick={() => setChanging(true)}>
            Change
          </Button>
          <Button
            variant="destructivePlain"
            icon={<TrashIcon size={18} weight="bold" />}
            busy={remove.isPending}
            onClick={() => void ask()}
          >
            Delete
          </Button>
        </div>
      }
    >
      {remove.isError && <Banner tone="error">{problem(remove.error)}</Banner>}
      <ListSection level={3}>
        <ListValue label="Done" detail={dayText(record.doneAt, true)} />
        <ListValue label="Logged" detail={record.source === 'mfd' ? 'On the MFD' : 'Here'} />
        {record.odometerKm !== null && <ListValue label="Odometer" detail={distance(record.odometerKm)} />}
        {record.mfdOdometerKm !== null && (
          <ListValue
            label="As the MFD counts"
            detail={distance(record.mfdOdometerKm)}
            subtitle={record.engineHours !== null ? `${grouped(record.engineHours, 1)} engine hours` : undefined}
          />
        )}
        {record.provider !== null && <ListValue label="Done by" detail={record.provider} />}
        {record.cost !== null && <ListValue label="Cost" detail={moneyText(record.cost)} />}
        {record.resetReminder && <ListValue label="Reminder" detail="Reset on the MFD" />}
      </ListSection>
      {record.lines.length > 0 && (
        <ListSection header="Parts and labour" level={3}>
          {record.lines.map((line, i) => (
            <ListValue key={i} label={lineWords(line)} />
          ))}
        </ListSection>
      )}
      {record.notes && (
        <ListSection header="Notes" level={3}>
          <ListValue label={<span className={s.notes}>{record.notes}</span>} />
        </ListSection>
      )}
    </Sheet>
  );
}

/** Logging a service: what was done, and whether the MFD's reminder starts again from it. */
function LogSheet({ car, units, open, onClose }: { car: Vehicle; units: Units; open: boolean; onClose: () => void }) {
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  const say = useToast();
  const id = useId();
  const idempotency = useIdempotencyKey();
  const log = useMutation({
    mutationFn: (body: ServiceIn) =>
      data(
        api.POST('/vehicles/{vehicleId}/service', {
          params: { path: { vehicleId: car.id }, header: { 'Idempotency-Key': idempotency.keyFor(body) } },
          body,
        }),
      ),
    onSuccess: async (record) => {
      idempotency.made();
      onClose();
      await queryClient.invalidateQueries({ queryKey: keys.service(car.id) });
      say(record.resetReminder ? 'Logged. The MFD resets its reminder at its next sync.' : 'Logged.');
    },
  });
  return (
    <Sheet
      open={open}
      onClose={() => {
        log.reset();
        onClose();
      }}
      title="Log a service"
      size="large"
      action={{ label: 'Log', form: id, busy: log.isPending }}
    >
      <ServiceForm id={id} units={units} failed={log.error} onSend={(body) => log.mutate(body as ServiceIn)} />
    </Sheet>
  );
}

/** A line as its fields hold it while it's typed. */
interface LineForm {
  key: number;
  description: string;
  quantity: string;
  partNumber: string;
  unitCost: string;
  labourCost: string;
}

const typedText = (n: number | null) => (n === null ? '' : String(n));

/** The service's fields: what was done and when, where, by whom and for how much, its parts and labour, and notes. */
function ServiceForm({
  id,
  units,
  record,
  failed,
  onSend,
}: {
  id: string;
  units: Units;
  record?: Record;
  failed: unknown;
  onSend: (body: ServiceIn | ServicePatch) => void;
}) {
  const [title, setTitle] = useState(record?.title ?? '');
  // Today, as the form opened: the latest day a service can have been done.
  const [today] = useState(() => dateField(Date.now()));
  const [date, setDate] = useState(() => (record ? dateField(record.doneAt) : today));
  const [odometer, setOdometer] = useState(
    record?.odometerKm != null ? String(Math.round(toDisplay(units, 'distance', record.odometerKm))) : '',
  );
  const [provider, setProvider] = useState(record?.provider ?? '');
  const [cost, setCost] = useState(typedText(record?.cost ?? null));
  const [notes, setNotes] = useState(record?.notes ?? '');
  const [lines, setLines] = useState<LineForm[]>(() =>
    (record?.lines ?? []).map((l, key) => ({
      key,
      description: l.description,
      quantity: typedText(l.quantity),
      partNumber: l.partNumber ?? '',
      unitCost: typedText(l.unitCost),
      labourCost: typedText(l.labourCost),
    })),
  );
  const [nextKey, setNextKey] = useState(record?.lines.length ?? 0);
  const [reset, setReset] = useState(false);
  const [errors, setErrors] = useState<{ [field: string]: string }>({});
  const unit = symbol(units, 'distance');

  const changeLine = (key: number, field: keyof Omit<LineForm, 'key'>, value: string) =>
    setLines((all) => all.map((l) => (l.key === key ? { ...l, [field]: value } : l)));
  const addLine = () => {
    setLines((all) => [
      ...all,
      { key: nextKey, description: '', quantity: '', partNumber: '', unitCost: '', labourCost: '' },
    ]);
    setNextKey((n) => n + 1);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const found: { [field: string]: string } = {};
    if (!title.trim()) {
      found.title = 'Say what was done, like "Oil and filter".';
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      found.date = 'Choose the day it was done.';
    }
    const shown = typedAmount(odometer);
    if (shown !== null && (!Number.isFinite(shown) || shown < 0)) {
      found.odometer = 'A distance, or nothing.';
    }
    const amount = (value: string, key: string) => {
      const n = typedAmount(value);
      if (n !== null && (!Number.isFinite(n) || n < 0 || n > 10_000_000)) {
        found[key] = 'An amount, or nothing.';
      }
      return n;
    };
    const total = amount(cost, 'cost');
    // A line left empty is no line.
    const kept = lines.filter((l) =>
      [l.description, l.quantity, l.partNumber, l.unitCost, l.labourCost].some((v) => v.trim()),
    );
    const sent: Line[] = kept.map((l) => {
      if (!l.description.trim()) {
        found[`${l.key}-description`] = 'Say what it was.';
      }
      const quantity = typedAmount(l.quantity);
      if (quantity !== null && (!Number.isFinite(quantity) || quantity < 0 || quantity > 100_000)) {
        found[`${l.key}-quantity`] = 'A number, or nothing.';
      }
      return {
        description: l.description.trim(),
        quantity,
        partNumber: l.partNumber.trim() || null,
        unitCost: amount(l.unitCost, `${l.key}-unitCost`),
        labourCost: amount(l.labourCost, `${l.key}-labourCost`),
      };
    });
    setErrors(found);
    if (Object.keys(found).length > 0) {
      return;
    }
    const body = {
      title: title.trim(),
      doneAt: noon(date),
      notes: notes.trim() === '' ? null : notes.trim(),
      odometerKm: shown === null ? null : fromDisplay(units, 'distance', shown),
      provider: provider.trim() || null,
      cost: total,
      lines: sent,
    };
    onSend(record ? body : { ...body, resetReminder: reset });
  };

  const added = linesTotal(lines);
  return (
    <form id={id} className={s.form} onSubmit={submit} noValidate>
      {failed !== null && failed !== undefined && <Banner tone="error">{problem(failed)}</Banner>}
      <TextField
        label="What was done"
        value={title}
        maxLength={100}
        autoComplete="off"
        onChange={(e) => setTitle(e.target.value)}
        error={errors.title}
        data-autofocus
      />
      <div className={s.pair}>
        <TextField
          label="Done on"
          type="date"
          value={date}
          max={today}
          onChange={(e) => setDate(e.target.value)}
          error={errors.date}
        />
        <TextField
          label={`Odometer (${unit})`}
          optional
          inputMode="decimal"
          autoComplete="off"
          hint="The car's own, if you have it."
          value={odometer}
          onChange={(e) => setOdometer(e.target.value)}
          error={errors.odometer}
        />
      </div>
      <div className={s.pair}>
        <TextField
          label="Done by"
          optional
          maxLength={100}
          hint="The workshop, or you."
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
        />
        <TextField
          label="Cost"
          optional
          inputMode="decimal"
          autoComplete="off"
          hint={`What it cost in all.${added > 0 ? ` The lines below add up to ${moneyText(added)}.` : ''}`}
          value={cost}
          onChange={(e) => setCost(e.target.value)}
          error={errors.cost}
        />
      </div>
      <fieldset className={s.lines}>
        <legend className={s.legend}>Parts and labour</legend>
        {lines.map((line, i) => (
          <fieldset key={line.key} className={s.line}>
            <legend className={s.lineLegend}>Line {i + 1}</legend>
            <IconButton
              className={s.removeLine}
              label={`Remove line ${i + 1}`}
              icon={<TrashIcon size={18} />}
              onClick={() => setLines((all) => all.filter((l) => l.key !== line.key))}
            />
            <TextField
              label="What it was"
              maxLength={200}
              value={line.description}
              onChange={(e) => changeLine(line.key, 'description', e.target.value)}
              error={errors[`${line.key}-description`]}
            />
            <div className={s.lineNumbers}>
              <TextField
                label="Quantity"
                inputMode="decimal"
                autoComplete="off"
                value={line.quantity}
                onChange={(e) => changeLine(line.key, 'quantity', e.target.value)}
                error={errors[`${line.key}-quantity`]}
              />
              <TextField
                label="Part number"
                maxLength={60}
                autoComplete="off"
                value={line.partNumber}
                onChange={(e) => changeLine(line.key, 'partNumber', e.target.value)}
              />
              <TextField
                label="Unit cost"
                inputMode="decimal"
                autoComplete="off"
                value={line.unitCost}
                onChange={(e) => changeLine(line.key, 'unitCost', e.target.value)}
                error={errors[`${line.key}-unitCost`]}
              />
              <TextField
                label="Labour"
                inputMode="decimal"
                autoComplete="off"
                value={line.labourCost}
                onChange={(e) => changeLine(line.key, 'labourCost', e.target.value)}
                error={errors[`${line.key}-labourCost`]}
              />
            </div>
          </fieldset>
        ))}
        <div>
          <Button
            variant="gray"
            size="small"
            icon={<PlusIcon size={16} weight="bold" />}
            disabled={lines.length >= 50}
            onClick={addLine}
          >
            Add a line
          </Button>
        </div>
      </fieldset>
      <TextArea
        label="Notes"
        optional
        rows={3}
        maxLength={2000}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      {!record && (
        <SwitchRow
          label="Reset the MFD's service reminder"
          detail="It counts to the next service from this one, once it next syncs."
          checked={reset}
          onChange={(e) => setReset(e.target.checked)}
        />
      )}
    </form>
  );
}
